import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  InputNumber,
  AccountingFormLayout,
  SearchableSelect,
} from "components";
import { usePaymentVoucher, useCustomer } from "hooks";
import {
  PaymentVoucherForm,
  PaymentVoucherItem,
  PaymentVoucherRow,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { ROUTES } from "utils";
import { Plus, Trash2 } from "lucide-react";
import { parseNumberSafe as normalizeNumber } from "utils/helpers/accounting";
import { sanitizePaymentVoucherItems } from "utils/helpers/sanitizers";
import {
  PAYMENT_REASON_OPTIONS,
  VOUCHER_STATUS_OPTIONS,
} from "constants/accountingReasons";

const todayString = () => new Date().toISOString().slice(0, 10);
const ATTACHMENT_SPLIT_REGEX = /[,\n]/;

const defaultFormValues: PaymentVoucherForm = {
  payment_date: todayString(),
  object_name: "",
  receiver_name: "",
  address: "",
  description: "",
  attachments: [],
  original_voucher_no: "",
  import_invoice_id: undefined,
  status: "draft",
  items: [],
};

const normalizeAttachments = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : String(item ?? "")))
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => (typeof item === "string" ? item : String(item ?? "")))
          .map((item) => item.trim())
          .filter(Boolean);
      }
    } catch {
      // ignore invalid JSON strings
    }

    return trimmed
      .split(ATTACHMENT_SPLIT_REGEX)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const sanitizeItems = (items?: PaymentVoucherItem[]): PaymentVoucherItem[] =>
  sanitizePaymentVoucherItems(items);

const PaymentVoucherDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { fetchDetail, createPaymentVoucher, updatePaymentVoucher } =
    usePaymentVoucher();
  const { data: customersData } = useCustomer().fetchList(
    { page: 1, pageSize: 200 },
    true,
  );

  const { data: detailResponse, isFetching: isFetchingDetail } = fetchDetail(
    id ?? "",
    isEdit,
  );

  const [form, setForm] = useState<PaymentVoucherForm>(defaultFormValues);
  const [attachmentsInput, setAttachmentsInput] = useState<string[]>([]);
  const [attachmentsText, setAttachmentsText] = useState<string>("");
  const [existingAttachments, setExistingAttachments] = useState<string[]>([]);
  const [items, setItems] = useState<PaymentVoucherItem[]>([]);

  useEffect(() => {
    if (!detailResponse?.data) return;
    const voucher = detailResponse.data as PaymentVoucherRow;
    setForm({
      payment_date: voucher.payment_date || todayString(),
      object_name: voucher.object_name || "",
      receiver_name: voucher.receiver_name || "",
      address: voucher.address || "",
      description: voucher.description || "",
      attachments: normalizeAttachments(voucher.attachments),
      original_voucher_no: voucher.original_voucher_no || "",
      import_invoice_id: voucher.import_invoice_id,
      status: (voucher.status as any) || "draft",
      items: sanitizeItems(voucher.items),
    });
    const normalizedAttachments = normalizeAttachments(voucher.attachments);
    setExistingAttachments(normalizedAttachments);
    setAttachmentsInput(normalizedAttachments);
    setAttachmentsText(normalizedAttachments.join(", "));
    setItems(
      sanitizeItems(voucher.items).length
        ? sanitizeItems(voucher.items)
        : [{ amount: 0, partner_id: "", reason: "", description: "" }],
    );
  }, [detailResponse]);

  useEffect(() => {
    if (isEdit) return;
    setForm(defaultFormValues);
    setItems([{ amount: 0, partner_id: "", reason: "", description: "" }]);
    setAttachmentsInput([]);
    setAttachmentsText("");
    setExistingAttachments([]);
  }, [isEdit]);

  const totalAmount = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.amount ?? 0) || 0), 0),
    [items],
  );

  const handleFormChange = useCallback(
    (key: keyof PaymentVoucherForm, value: any) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleAddItem = useCallback(() => {
    setItems((prev) => [
      ...prev,
      { amount: 0, partner_id: "", reason: "", description: "" },
    ]);
  }, []);

  const handleItemChange = useCallback(
    (index: number, key: keyof PaymentVoucherItem, value: any) => {
      setItems((prev) =>
        prev.map((item, idx) =>
          idx === index
            ? {
                ...item,
                [key]: key === "amount" ? normalizeNumber(value) : value,
              }
            : item,
        ),
      );
    },
    [],
  );

  // Auto-fill debit/credit accounts when reason_code is selected
  const handleReasonCodeChange = useCallback(
    (index: number, reasonCode: string) => {
      const reasonOption = PAYMENT_REASON_OPTIONS.find(
        (opt) => opt.value === reasonCode,
      );
      setItems((prev) =>
        prev.map((item, idx) =>
          idx === index
            ? {
                ...item,
                reason_code: reasonCode,
                reason: reasonOption?.label ?? item.reason,
                debit_account: reasonOption?.debitAccount ?? item.debit_account,
                credit_account:
                  reasonOption?.creditAccount ?? item.credit_account,
              }
            : item,
        ),
      );
    },
    [],
  );

  useEffect(() => {
    const parsed = attachmentsText
      .split(ATTACHMENT_SPLIT_REGEX)
      .map((v) => v.trim())
      .filter(Boolean);
    setAttachmentsInput(parsed);
  }, [attachmentsText]);

  // Convert customers to SearchableSelect options format
  const customerOptions = useMemo(
    () =>
      (customersData?.data as any[])?.map((c) => ({
        value: c.id,
        label: `${c.code}${c.name ? ` - ${c.name}` : ""}`,
        searchData: c.name,
      })) ?? [],
    [customersData?.data],
  );

  const handleRemoveItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  const buildPayload = useCallback((): PaymentVoucherForm | null => {
    if (!form.payment_date || !form.object_name || !form.receiver_name) {
      SnackbarUtils.error("Ngày chi, Đối tượng và Người nhận là bắt buộc");
      return null;
    }

    const normalizedItems = sanitizeItems(items);

    if (!normalizedItems.length || !normalizedItems[0].amount) {
      SnackbarUtils.error("Vui lòng nhập ít nhất một dòng chi tiết và số tiền");
      return null;
    }

    return {
      ...form,
      attachments:
        attachmentsInput.length > 0
          ? normalizeAttachments(attachmentsInput)
          : existingAttachments,
      items: normalizedItems,
    };
  }, [attachmentsInput, existingAttachments, form, items]);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      const payload = buildPayload();
      if (!payload) return;

      if (isEdit && id) {
        updatePaymentVoucher.mutate(
          { id, ...payload },
          {
            onSuccess: (res) => {
              if (res?.data) {
                SnackbarUtils.success("Cập nhật phiếu chi thành công");
              }
              navigate(ROUTES.ACCOUNTING.CASH_PAYMENT);
            },
          },
        );
        return;
      }

      createPaymentVoucher.mutate(payload, {
        onSuccess: () => {
          navigate(ROUTES.ACCOUNTING.CASH_PAYMENT);
        },
      });
    },
    [
      buildPayload,
      createPaymentVoucher,
      id,
      isEdit,
      navigate,
      updatePaymentVoucher,
    ],
  );

  const isSaving =
    createPaymentVoucher.isPending || updatePaymentVoucher.isPending;

  return (
    <AccountingFormLayout
      title="Phiếu chi"
      subtitle="Nhập thông tin chung và danh sách dòng chi tiết."
      isEdit={isEdit}
      isSaving={isSaving}
      isLoading={isFetchingDetail}
      backUrl={ROUTES.ACCOUNTING.CASH_PAYMENT}
      backLabel="Danh sách phiếu chi"
      onSubmit={handleSubmit}
      submitLabel="Lưu phiếu chi"
      showAddItemButton={true}
      onAddItem={handleAddItem}
      addItemLabel="Thêm dòng"
      totalAmount={totalAmount}
    >
      {/* General Information Card */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-4 py-3">
          <h2 className="text-base font-semibold text-gray-900">
            Thông tin chung
          </h2>
        </div>
        <div className="grid gap-4 px-4 py-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Đối tượng *
            </label>
            <input
              value={form.object_name}
              onChange={(e) => handleFormChange("object_name", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
              placeholder="Khách lẻ"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Người nhận *
            </label>
            <input
              value={form.receiver_name}
              onChange={(e) =>
                handleFormChange("receiver_name", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
              placeholder="Nhập người nhận"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Ngày chi *
            </label>
            <input
              type="date"
              value={form.payment_date}
              onChange={(e) => handleFormChange("payment_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Số phiếu
            </label>
            <input
              value={form.original_voucher_no ?? ""}
              onChange={(e) =>
                handleFormChange("original_voucher_no", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
              placeholder="TM00001"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Trạng thái
            </label>
            <select
              value={form.status ?? "draft"}
              onChange={(e) => handleFormChange("status", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
            >
              {VOUCHER_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Địa chỉ</label>
            <input
              value={form.address ?? ""}
              onChange={(e) => handleFormChange("address", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
              placeholder="Nhập địa chỉ"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Kèm theo (nhiều tệp)
            </label>
            <div className="space-y-2">
              <input
                type="text"
                value={attachmentsText}
                onChange={(e) => setAttachmentsText(e.target.value)}
                placeholder="tệp1.png, tệp2.pdf"
                className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
              />
              {(attachmentsInput.length > 0 ||
                existingAttachments.length > 0) && (
                <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                  <p className="text-xs font-semibold text-gray-600 mb-1">
                    Tệp đã chọn:
                  </p>
                  <ul className="space-y-1 text-xs text-gray-700 list-disc list-inside">
                    {(attachmentsInput.length > 0
                      ? attachmentsInput
                      : existingAttachments
                    ).map((item, idx) => (
                      <li key={`${idx}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-2 md:col-span-2">
            <label className="text-sm font-medium text-gray-700">
              Diễn giải
            </label>
            <textarea
              value={form.description ?? ""}
              onChange={(e) => handleFormChange("description", e.target.value)}
              rows={2}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
              placeholder="Nội dung chi"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Số chứng từ gốc
            </label>
            <input
              type="number"
              value={form.import_invoice_id ?? ""}
              onChange={(e) =>
                handleFormChange(
                  "import_invoice_id",
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
              placeholder="Nhập số chứng từ gốc"
            />
          </div>
        </div>
      </div>

      {/* Details Table */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-base font-semibold text-gray-900">
            Thông tin chi tiết
          </h2>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-gray-500">Tổng tiền</p>
              <p className="text-base font-semibold text-gray-900">
                {totalAmount.toLocaleString("vi-VN")} đ
              </p>
            </div>
            <button
              type="button"
              onClick={handleAddItem}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Thêm dòng
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="px-3 py-2 text-left font-semibold w-12">STT</th>
                <th className="px-3 py-2 text-right font-semibold min-w-[150px]">
                  Số tiền
                </th>
                <th className="px-3 py-2 text-left font-semibold min-w-[200px]">
                  Khách hàng/NCC
                </th>
                <th className="px-3 py-2 text-left font-semibold min-w-[200px]">
                  Lý do chi
                </th>
                <th className="px-3 py-2 text-left font-semibold w-24">
                  TK Nợ
                </th>
                <th className="px-3 py-2 text-left font-semibold w-24">
                  TK Có
                </th>
                <th className="px-3 py-2 text-left font-semibold min-w-[150px]">
                  Diễn giải
                </th>
                <th className="px-3 py-2 text-center font-semibold w-20">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {items.length ? (
                items.map((item, index) => (
                  <tr key={`item-${index}`} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                    <td className="px-3 py-2 text-right text-gray-900">
                      <InputNumber
                        value={item.amount ?? 0}
                        onValueChange={(values) =>
                          handleItemChange(
                            index,
                            "amount",
                            values.floatValue ?? 0,
                          )
                        }
                        className="h-9 w-full px-2 py-1 text-right text-sm"
                        suffix=" ₫"
                      />
                    </td>
                    <td className="px-3 py-2 text-gray-900">
                      <SearchableSelect
                        options={customerOptions}
                        value={item.partner_id}
                        onChange={(value) =>
                          handleItemChange(index, "partner_id", value)
                        }
                        placeholder="Chọn đối tượng"
                        clearable
                      />
                    </td>
                    <td className="px-3 py-2 text-gray-900">
                      <select
                        value={item.reason_code ?? ""}
                        onChange={(e) =>
                          handleReasonCodeChange(index, e.target.value)
                        }
                        className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
                      >
                        <option value="">Chọn lý do</option>
                        {PAYMENT_REASON_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-gray-900">
                      <input
                        value={item.debit_account ?? ""}
                        onChange={(e) =>
                          handleItemChange(
                            index,
                            "debit_account",
                            e.target.value,
                          )
                        }
                        className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
                      />
                    </td>
                    <td className="px-3 py-2 text-gray-900">
                      <input
                        value={item.credit_account ?? ""}
                        onChange={(e) =>
                          handleItemChange(
                            index,
                            "credit_account",
                            e.target.value,
                          )
                        }
                        className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
                      />
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      <input
                        value={item.description ?? ""}
                        onChange={(e) =>
                          handleItemChange(index, "description", e.target.value)
                        }
                        className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
                        placeholder="Diễn giải"
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        disabled={items.length === 1}
                      >
                        <Trash2 className="h-4 w-4" />
                        Xóa
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    Chưa có dòng chi tiết. Thêm mới để bắt đầu.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-end px-4 py-3 border-t border-gray-100 bg-gray-50">
          <p className="text-sm text-gray-600">
            Tổng cộng:{" "}
            <span className="font-semibold text-gray-900">
              {totalAmount.toLocaleString("vi-VN")} đ
            </span>
          </p>
        </div>
      </div>
    </AccountingFormLayout>
  );
};

export default PaymentVoucherDetail;
